/**
 * Trace sampling policy for Sentry performance monitoring.
 *
 * The map issues a handful of very high-frequency transactions (continuous
 * realtime polling and per-hover lookups). Capturing every one of them exhausts
 * the span/transaction quota and drowns out the transactions that actually
 * matter (page loads, navigations, user-initiated fetches).
 *
 * Rather than dropping these entirely, we keep a small representative sample of
 * them so trends stay visible, sample page loads at ~50% and everything else
 * at 100%.
 *
 * Map tile requests are excluded from `http.client` spans altogether (see
 * `shouldCreateSpanForRequest`).
 */

/**
 * Sample rate for page loads (~50%). Each page load carries ~45 resource
 * spans (JS chunks, CSS, preloads), so this halves that volume while leaving
 * plenty of samples for Web Vitals scores.
 */
export const PAGELOAD_SAMPLE_RATE = 0.5;

/**
 * Resource span ops passed to `browserTracingIntegration`'s
 * `ignoreResourceSpans`. Image resources are mostly map tiles (e.g.
 * openseamap seamarks) and say nothing about app performance; scripts,
 * stylesheets and module preloads are kept for page-load analysis.
 */
export const IGNORED_RESOURCE_SPAN_OPS = ['resource.img'];

/**
 * Sample rate for the continuous 1-5s map pollers (~5%). Even at 10% these
 * produced ~800K spans/week, so they are halved again while still keeping a
 * usable latency trend.
 */
export const CONTINUOUS_POLLER_SAMPLE_RATE = 0.05;

/**
 * Transaction names for the continuous map pollers. These correspond to the
 * `operationName` passed to `useTrackedQuery` / `trackOperation`:
 * - `overlay:get`            — map overlay, refetched every ~5s while open
 * - `ship:monitorSelections` — ship position poll, every 5s in background
 * - `ship:trackPoll`         — live track poll, every 1s while tracking
 */
export const CONTINUOUS_POLLER_TRANSACTION_NAMES = new Set<string>([
  'overlay:get',
  'ship:monitorSelections',
  'ship:trackPoll',
]);

/** Sample rate applied to the other high-frequency polls/lookups (~10%). */
export const HIGH_FREQUENCY_SAMPLE_RATE = 0.1;

/**
 * Transaction names for bursty or periodic operations that are too frequent
 * to capture in full:
 * - `ship.get`             — hover ship lookup, one per hover
 * - `shipInfo:atPixel`     — map click/hover lookup
 * - `events:getForPeriod`  — historical events, every 30s (plus retries)
 * - `grounding:list`       — grounding events poll
 * - `s2s:listCurrent`      — S2S events poll
 */
export const HIGH_FREQUENCY_TRANSACTION_NAMES = new Set<string>([
  'ship.get',
  'shipInfo:atPixel',
  'events:getForPeriod',
  'grounding:list',
  's2s:listCurrent',
]);

interface TraceSamplingContext {
  name?: string;
  attributes?: Record<string, unknown>;
}

/**
 * Resolve the trace sample rate for a transaction.
 *
 * Auth transactions are always kept (user identity) and page loads are sampled
 * at ~50% (Core Web Vitals). The continuous map pollers are sampled at ~5%, other known
 * high-frequency operations at ~10%, and everything else at 100%.
 */
export function getTraceSampleRate({
  name,
  attributes,
}: TraceSamplingContext): number {
  const op = attributes?.['sentry.op'];

  // Always capture auth transactions (user identity tracking).
  if (op === 'auth') return 1.0;

  if (op === 'pageload') return PAGELOAD_SAMPLE_RATE;

  if (name && CONTINUOUS_POLLER_TRANSACTION_NAMES.has(name)) {
    return CONTINUOUS_POLLER_SAMPLE_RATE;
  }

  if (name && HIGH_FREQUENCY_TRANSACTION_NAMES.has(name)) {
    return HIGH_FREQUENCY_SAMPLE_RATE;
  }

  // Everything else (navigations, user-initiated fetches, one-off queries).
  return 1.0;
}

// XYZ vector/raster tiles (`/{z}/{x}/{y}.pbf`) and WMS/WMTS tile fetches. Tile
// services are user-configurable, so match on URL shape rather than host.
const XYZ_TILE_PATTERN =
  /\/\d+\/\d+\/\d+\.(?:pbf|mvt|png|jpe?g|webp)(?:[?#]|$)/i;
const OGC_TILE_REQUEST_PATTERN = /[?&]request=(?:getmap|gettile)(?:&|$)/i;

export function isMapTileRequest(url: string): boolean {
  return XYZ_TILE_PATTERN.test(url) || OGC_TILE_REQUEST_PATTERN.test(url);
}

/**
 * `browserTracingIntegration` hook: skip `http.client` spans for map tiles.
 * A single pan/zoom fetches dozens of tiles, which accounted for 200K+
 * spans/week without telling us anything about our own services.
 */
export function shouldCreateSpanForRequest(url: string): boolean {
  return !isMapTileRequest(url);
}
